import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppointmentReadCollectionResource } from '../models/appointment-model';
import { Usage } from '../models/api/responses/personal-area.response';
import {
  DoctorPersonalArea,
  ClineaseRoleResponse,
  FiscalProfileOutput,
  Business,
} from '../models/doctor-model';
import {
  DoctorFiscalIdRequestModel,
  StsProfileOutput,
  StsProfileUpdateInput,
  StsProfileCreateInput,
  StsProfileOperationOutput,
} from '../models/api/billing-data.api.model';

@Injectable({
  providedIn: 'root',
})
export class DoctorService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private getSessionToken(): string | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    return sessionStorage.getItem('psicogeniusAccessToken');
  }

  addHeader(): HttpHeaders {
    const token = this.getSessionToken();

    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  getFiscalProfile(): Observable<FiscalProfileOutput> {
    const endpoint = this.apiUrl + '/me/fiscal-profile';
    const headers = this.addHeader();
    return this.http.get<FiscalProfileOutput>(endpoint, { headers });
  }

  deleteDoctor(): Observable<any> {
    const endpoint = this.apiUrl + '/doctors';
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers });
  }

  getDoctorPersonalArea(): Observable<DoctorPersonalArea> {
    const endpoint = this.apiUrl + '/me/personal-area';
    const headers = this.addHeader();
    return this.http.get<DoctorPersonalArea>(endpoint, { headers });
  }

  getDoctorFiscalCode(): Observable<DoctorFiscalIdRequestModel> {
    const endpoint = this.apiUrl + '/me/fiscal-code';
    const headers = this.addHeader();
    return this.http.get<DoctorFiscalIdRequestModel>(endpoint, { headers });
  }

  updateDoctorFiscalProfile(data: FiscalProfileOutput): Observable<any> {
    const endpoint = this.apiUrl + '/me/fiscal-profile';
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  createStsProfile(
    data: StsProfileCreateInput
  ): Observable<StsProfileOperationOutput> {
    const endpoint = this.apiUrl + '/me/sts-profile';
    const headers = this.addHeader();
    return this.http.post<StsProfileOperationOutput>(endpoint, data, {
      headers,
    });
  }

  updateStsProfile(
    data: StsProfileUpdateInput
  ): Observable<StsProfileOperationOutput> {
    const endpoint = this.apiUrl + '/me/sts-profile';
    const headers = this.addHeader();
    return this.http.patch<StsProfileOperationOutput>(endpoint, data, {
      headers,
    });
  }

  getStsProfile(): Observable<StsProfileOutput> {
    const endpoint = this.apiUrl + '/me/sts-profile';
    const headers = this.addHeader();
    return this.http.get<StsProfileOutput>(endpoint, { headers });
  }

  updateDoctorPersonalArea(data: { [key: string]: any }): Observable<any> {
    const endpoint = this.apiUrl + '/me/personal-area';
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  updateDoctorFiscalCode(data: DoctorFiscalIdRequestModel): Observable<any> {
    const endpoint = this.apiUrl + '/me/fiscal-code';
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  getModules(): Observable<any> {
    const endpoint = this.apiUrl + '/modules';
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  createNewModule(data: string, file: File): Observable<any> {
    const endpoint = this.apiUrl + '/modules/upload_and_create';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('data', data);
    const headers = this.addHeader();
    return this.http.post(endpoint, formData, { headers });
  }

  getDocumentFile(moduleId: string): Observable<Blob> {
    const endpoint = this.apiUrl + '/modules/download/' + moduleId;
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers: headers, responseType: 'blob' });
  }

  deleteModule(moduleId: string): Observable<any> {
    const endpoint = this.apiUrl + '/modules/' + moduleId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers });
  }

  putModule(moduleId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/modules/' + moduleId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers });
  }

  getTodayAppointments(): Observable<any> {
    const endpoint = this.apiUrl + '/appointments/today';
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  getAllAppointments(): Observable<AppointmentReadCollectionResource[]> {
    const endpoint = this.apiUrl + '/doctors_all_appointments';
    const headers = this.addHeader();
    return this.http.get<AppointmentReadCollectionResource[]>(endpoint, {
      headers,
    });
  }

  getAllPatients(): Observable<any> {
    const endpoint = this.apiUrl + '/patients';
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  getDoctorRole(): Observable<ClineaseRoleResponse> {
    const endpoint = this.apiUrl + '/sub/user-role';
    const headers = this.addHeader();
    return this.http.get<ClineaseRoleResponse>(endpoint, { headers });
  }

  getUsage(): Observable<Usage> {
    const endpoint = this.apiUrl + '/me/usage';
    const headers = this.addHeader();
    return this.http.get<Usage>(endpoint, { headers });
  }

  createBusiness(body: Business): Observable<Business> {
    const endpoint = this.apiUrl + '/me/business';
    const headers = this.addHeader();
    return this.http.post<Business>(endpoint, body, { headers });
  }
}
